import { MouseEvent, useCallback, useEffect, useState } from "react";
import {
  getMessageNotifications,
  markMessageRead,
  MESSAGES_CHANGED,
  MessageNotifications,
} from "../../ApiService/admin/Message";
import { useAdminLang } from "./i18n";
import { formatDateTime, timeAgo } from "./time";

const POLL_MS = 30_000;
const prevent = (e: MouseEvent) => e.preventDefault();

/**
 * Bell of the template header (notifications-dropdown). The red dot shows while there are
 * unread contact messages; opening a message ("view") marks it read and clears it.
 *
 * custom.js wraps #header-notification-scroll with SimpleBar, which moves its children,
 * so that div and the <ul> inside it are always rendered and only the <li>s change.
 * The template's ".dropdown-item-close1" handler removes DOM nodes, so it isn't used here.
 */
export default function HeaderNotifications() {
  const { t, lang } = useAdminLang();
  const [data, setData] = useState<MessageNotifications>({ unreadCount: 0, latest: [] });
  const [loaded, setLoaded] = useState(false);

  const load = useCallback(() => {
    getMessageNotifications()
      .then((result) => {
        setData(result);
        setLoaded(true);
      })
      .catch((err) => console.error(err));
  }, []);

  useEffect(() => {
    load();
    const timer = window.setInterval(() => {
      if (document.visibilityState === "visible") load();
    }, POLL_MS);
    window.addEventListener("focus", load);
    window.addEventListener(MESSAGES_CHANGED, load);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener("focus", load);
      window.removeEventListener(MESSAGES_CHANGED, load);
    };
  }, [load]);

  const dismiss = (e: MouseEvent, id: number) => {
    e.preventDefault();
    e.stopPropagation();
    // optimistic: the list refreshes from the server right after
    setData((prev) => ({ unreadCount: Math.max(prev.unreadCount - 1, 0), latest: prev.latest.filter((m) => m.id !== id) }));
    markMessageRead(id).catch((err) => {
      console.error(err);
      load();
    });
  };

  const count = data.unreadCount;
  const countText = lang === "en" ? `${count} unread` : `${count.toLocaleString("fa-IR")} خوانده نشده`;

  return (
    <li className="header-element notifications-dropdown dropdown">
      <a
        href="#"
        onClick={prevent}
        className="header-link dropdown-toggle"
        data-bs-toggle="dropdown"
        data-bs-auto-close="outside"
        id="messageDropdown"
        aria-expanded="false"
        aria-label={count > 0 ? `${t("هشدارها")}: ${countText}` : t("هشدارها")}
        title={count > 0 ? countText : t("هشدارها")}
      >
        <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6 header-link-icon" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" d="M14.857 17.082a23.848 23.848 0 0 0 5.454-1.31A8.967 8.967 0 0 1 18 9.75V9A6 6 0 0 0 6 9v.75a8.967 8.967 0 0 1-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 0 1-5.714 0m5.714 0a3 3 0 1 1-5.714 0M3.124 7.5A8.969 8.969 0 0 1 5.292 3m13.416 0a8.969 8.969 0 0 1 2.168 4.5"></path>
        </svg>
        {" "}
        {count > 0 && <span className="header-icon-pulse bg-danger rounded pulse pulse-danger"></span>}
      </a>
      <div className="main-header-dropdown dropdown-menu dropdown-menu-end" data-popper-placement="none">
        <div className="p-3">
          <div className="d-flex align-items-center justify-content-between">
            <p className="mb-0 fs-15 fw-medium">{t("هشدارها")}</p>
            <span className={`badge text-fixed-white ${count > 0 ? "bg-danger" : "bg-secondary"}`}>{countText}</span>
          </div>
        </div>
        <div className="dropdown-divider"></div>
        <div id="header-notification-scroll">
          <ul className="list-unstyled mb-0">
            {data.latest.map((m) => (
              <li className="dropdown-item" key={m.id}>
                <div className="d-flex align-items-center">
                  <div className="pe-2 lh-1">
                    <span className="avatar avatar-md avatar-rounded bg-primary-transparent fw-semibold">
                      {(m.name || "?").trim().charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <div className="flex-grow-1 d-flex align-items-center justify-content-between">
                    <div className="min-w-0">
                      <p className="mb-0 fw-medium">
                        <a href={`/admin/messages?view=${m.id}`}>{m.name}</a>
                      </p>
                      <div className="text-muted fw-normal fs-12 header-notification-text text-truncate">{m.subject}</div>
                      <div className="fw-normal fs-10 text-muted op-8" title={formatDateTime(m.createDate, lang)}>
                        {timeAgo(m.createDate, lang)}
                      </div>
                    </div>
                    <div>
                      <a href="#" onClick={(e) => dismiss(e, m.id)} className="min-w-fit-content" title={t("علامت خوانده‌شده")}>
                        <i className="ri-close-line"></i>
                      </a>
                    </div>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
        {count > 0 ? (
          <div className="p-3 border-top">
            <div className="d-grid">
              <a href="/admin/messages" className="btn btn-primary btn-wave">{t("مشاهده همه پیام‌ها")}</a>
            </div>
          </div>
        ) : (
          <div className="p-5">
            <div className="text-center">
              <span className="avatar avatar-xl avatar-rounded bg-secondary-transparent">
                <i className="ri-notification-off-line fs-2"></i>
              </span>
              <h6 className="fw-medium mt-3">{loaded ? t("پیام خوانده‌نشده‌ای وجود ندارد") : t("در حال بارگذاری...")}</h6>
              <a href="/admin/messages" className="fs-13">{t("مشاهده همه پیام‌ها")}</a>
            </div>
          </div>
        )}
      </div>
    </li>
  );
}
